// API приложения курьера. Суммы — в копейках, количества — в единицах товара.

import { apiUpload, del, get, patch, post } from '@/lib/api';

export type PayMethod = 'CASH' | 'CARD' | 'TRANSFER' | 'OTHER';

/** Так визит записан в учёте: DEBT — за отгрузку ничего не получили. */
export type VisitMethod = PayMethod | 'DEBT';

export const PAY_METHOD_LABEL: Record<VisitMethod, string> = {
  CASH: 'Наличные',
  CARD: 'Карта',
  TRANSFER: 'Перевод',
  OTHER: 'Другое',
  DEBT: 'В долг',
};

/** Как магазин рассчитывается — закрепил руководитель, курьер не меняет. */
export type PayMode = 'FREE' | 'CASH' | 'DEBT';

export const PAY_MODE_LABEL: Record<Exclude<PayMode, 'FREE'>, string> = {
  CASH: 'Только наличка',
  DEBT: 'Только в долг',
};

// ─── Вход ───────────────────────────────────────────────────────────────────

export interface Tokens {
  access_token: string;
  refresh_token: string;
}

export const requestCode = (email: string) => post<{ sentAt: string; nextAt: string }>('/auth/request-code', { email }, { silent: true });
export const verifyCode = (email: string, code: string) => post<Tokens>('/auth/verify', { email, code }, { silent: true });

// ─── Профиль ────────────────────────────────────────────────────────────────

export interface Profile {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  avatarUrl: string | null;
  notifyChat: boolean;
  activeShiftId: string | null;
  createdAt: string;
  wholesaler: { id: string; name: string; logoUrl: string | null; city: string | null };
}

export const getProfile = () => get<Profile>('/me');
export const updateProfile = (input: Partial<{ name: string; phone: string | null; notifyChat: boolean }>) => patch<Profile>('/me', input);
export const removeAvatar = () => del<{ avatarUrl: null }>('/me/avatar');

// ─── Маршруты и смена ───────────────────────────────────────────────────────

export interface CityRoutes {
  id: string;
  name: string;
  routes: Array<{ id: string; name: string; comment: string | null; pointsCount: number; busyBy: { id: string; name: string } | null }>;
}

export interface ShiftPoint {
  id: string;
  name: string;
  address: string | null;
  phone: string | null;
  comment: string | null;
  debt: number;
  lat: number | null;
  lon: number | null;
  /** Режим работы магазина, если точка из справочника. */
  hours?: string | null;
  payMode?: PayMode;
  payMethods?: PayMethod[];
  debtLimit?: number | null;
  visit: {
    id: string;
    pointId: string;
    status: 'OPEN' | 'DONE';
    openedAt: string;
    closedAt: string | null;
    deliveredTotal: number;
    returnedTotal: number;
    dueAmount: number;
    paidAmount: number;
    debtAfter: number;
    /** Фото витрины уже снято — точка начата. */
    hasPhoto?: boolean;
  } | null;
}

/** Правила руководителя для курьеров. */
export interface CourierSettings {
  /** Фото витрины обязательно при отметке точки. */
  shelfPhoto: boolean;
  /** Точку можно закрыть без геопозиции. */
  shipWithoutGps?: boolean;
  /** Можно отдать больше, чем выдано по накладной. */
  sellBeyondLoad?: boolean;
}

export interface MenuItem {
  id: string;
  name: string;
  unit: string;
  price: number;
}

export interface ActiveShift {
  shift: {
    id: string;
    routeId: string;
    routeName: string;
    cityName: string;
    startedAt: string;
    pointsTotal: number;
    pointsDone: number;
    deliveredTotal: number;
    returnedTotal: number;
    collectedTotal: number;
    debtTotal: number;
    exchangeCost?: number;
  };
  points: ShiftPoint[];
  menu: MenuItem[];
  /** Накладная смены: что выдано, отдано, заменено и сколько ещё в машине. */
  load: ShiftLoad | null;
  settings?: CourierSettings;
}

export interface ShiftLoad {
  id: string;
  number: number;
  lines: Array<{ itemId: string; name: string; unit: string; issued: number; delivered: number; replaced: number; returned: number; remaining: number }>;
}

export interface AvailableLoad {
  id: string;
  number: number;
  issuedAt: string;
  comment: string | null;
  personal: boolean;
  route: { id: string; name: string; isActive: boolean; city: { name: string } } | null;
  lines: Array<{ itemId: string; nameSnapshot: string; unitSnapshot: string; issuedQty: number }>;
}

export const listRoutes = () => get<CityRoutes[]>('/routes');
export const listLoads = () => get<AvailableLoad[]>('/loads');
export const getShift = () => get<{ active: ActiveShift | null }>('/shift');
export const startShift = (routeId: string, clientRequestId: string, loadId?: string) =>
  post<{ active: ActiveShift | null }>('/shift/start', { routeId, clientRequestId, ...(loadId ? { loadId } : {}) });

// ─── Точка ──────────────────────────────────────────────────────────────────

export interface PointScreen {
  shift: { id: string; routeName: string; cityName: string; loadNumber: number | null };
  point: { id: string; name: string; address: string | null; phone: string | null; comment: string | null; debt: number; lat: number | null; lon: number | null };
  /** Отметить точку можно в этом радиусе от её координат. */
  radiusM: number;
  /** Без фото витрины точку не отметить (настройка руководителя). */
  photoRequired?: boolean;
  /** false — отгрузка без проверки GPS. */
  gpsRequired?: boolean;
  /** true — можно отдать больше, чем в накладной. */
  sellBeyondLoad?: boolean;
  /** Режим расчёта магазина. Нет поля — старый сервер, значит FREE. */
  payMode?: PayMode;
  /** Способы, которые можно выбрать. Пусто при «только в долг». */
  payMethods?: PayMethod[];
  hours?: string | null;
  /** Лимит долга магазина, копейки. null — без лимита. */
  debtLimit?: number | null;
  /** Долг других точек этого же магазина. */
  otherDebt?: number;
  visit: {
    id: string;
    status: 'OPEN' | 'DONE';
    openedAt: string;
    closedAt: string | null;
    deliveredTotal: number;
    returnedTotal: number;
    dueAmount: number;
    paidAmount: number;
    paymentMethod: VisitMethod | null;
    debtBefore: number;
    debtAfter: number;
    comment: string | null;
    exchangeCost: number;
    photoUrl: string | null;
    distanceM: number | null;
    lines: Array<{
      itemId: string | null;
      nameSnapshot: string;
      unitSnapshot: string;
      deliveredQty: number;
      returnedQty: number;
      replacedQty: number;
      price: number;
    }>;
  } | null;
  /** Что можно отдать: по накладной — inCar (сколько в машине), без неё — null. */
  menu: Array<MenuItem & { inCar: number | null; returnable: number }>;
  /** Что можно забрать у точки: отгружено на неё за 45 дней и не возвращено. */
  returnables: Array<{ id: string; name: string; unit: string; price: number; returnable: number }>;
  recent: Array<{ id: string; closedAt: string | null; deliveredTotal: number; returnedTotal: number; paidAmount: number; courier: { name: string } }>;
}

export interface VisitSwap {
  fromItemId: string;
  qty: number;
  toItemId: string;
  toQty: number;
}

export interface GeoFix {
  lat: number;
  lon: number;
  accuracy: number | null;
}

export interface VisitCloseBody {
  lines: Array<{ itemId: string; delivered: number }>;
  /** Возврат товаром: тем же (замена) или другим (обмен). Деньги не двигаются. */
  swaps: VisitSwap[];
  geo?: GeoFix;
  paidAmount: number;
  paymentMethod: PayMethod;
  comment?: string | null;
  clientRequestId: string;
  at: string;
}

export const getPoint = (pointId: string) => get<PointScreen>(`/points/${pointId}`);
export const openPoint = (pointId: string) => post<PointScreen>(`/points/${pointId}/open`, undefined, { silent: true });
export const closePoint = (pointId: string, body: VisitCloseBody) => post<PointScreen>(`/points/${pointId}/close`, body, { silent: true });
export const reopenPoint = (pointId: string) => post<PointScreen>(`/points/${pointId}/reopen`);
export const finishShift = (body: { comment?: string | null; clientRequestId: string; report?: Array<{ itemId: string; leftover: number }> }) =>
  post<{ finished: unknown }>('/shift/finish', body, { silent: true });

export function uploadPointPhoto(pointId: string, blob: Blob, name: string, onProgress?: (r: number) => void) {
  const form = new FormData();
  form.append('file', blob, name);
  return apiUpload<{ photoUrl: string }>(`/points/${pointId}/photo`, form, { silent: true, onProgress });
}

// ─── Аналитика ──────────────────────────────────────────────────────────────

export interface MyStats {
  shifts: number;
  onlineMs: number;
  avgShiftMs: number;
  pointsDone: number;
  pointsTotal: number;
  delivered: number;
  returned: number;
  sold: number;
  returnRatePct: number;
  collected: number;
  debt: number;
  /** Себестоимость выданного взамен — потери от возвратов и обменов. */
  exchangeCost: number;
  avgPointMs: number;
  avgCheck: number;
}

export interface MyAnalytics {
  range: { from: string; to: string };
  summary: MyStats;
  points: Array<{
    pointId: string;
    name: string;
    visits: number;
    delivered: number;
    returned: number;
    sold: number;
    exchangeCost: number;
    collected: number;
    debt: number;
    lastAt: string | null;
    currentDebt: number;
  }>;
  shifts: Array<{
    id: string;
    startedAt: string;
    finishedAt: string | null;
    status: 'ACTIVE' | 'FINISHED';
    pointsDone: number;
    pointsTotal: number;
    deliveredTotal: number;
    returnedTotal: number;
    collectedTotal: number;
    debtTotal: number;
    routeName: string;
    cityName: string;
    durationMs: number;
  }>;
}

export const getMyAnalytics = (period: { from?: string; to?: string }) => get<MyAnalytics>('/analytics', period);
